import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

/** Taille de page des listes paginées côté client (produits, utilisateurs). */
export const TABLE_PAGE_SIZE = 50;

/** Ramène la page dans les bornes quand la liste a rétréci (filtre, rechargement). */
export const clampPage = (page: number, pageSize: number, total: number) =>
  Math.min(page, Math.max(0, Math.ceil(total / pageSize) - 1));

/** Première, dernière, et les voisines de la page courante ; `gap` pour « … ». */
const pageItems = (current: number, count: number): (number | "gap")[] => {
  const pages = Array.from(
    new Set([0, current - 1, current, current + 1, count - 1])
  )
    .filter((p) => p >= 0 && p < count)
    .sort((a, b) => a - b);
  return pages.flatMap((p, i): (number | "gap")[] =>
    i > 0 && p - pages[i - 1] > 1 ? ["gap", p] : [p]
  );
};

const NavButton = ({
  label,
  disabled,
  onClick,
  children,
  className = "",
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
  className?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    className={`rounded border border-grey-medium px-3 py-1 text-default-text disabled:opacity-40 ${className}`}
  >
    {children}
  </button>
);

type PaginationProps = {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  /**
   * Faux : ni total ni numéros de page, qui le laisseraient deviner. Le volume n'est
   * montré qu'aux comptes qui y ont droit (admin, PSC).
   */
  showTotal?: boolean;
};

// Le compteur reste visible en mobile : c'est lui qui situe.
export const Pagination = ({
  page,
  pageSize,
  total,
  onPageChange,
  showTotal = true,
}: PaginationProps) => {
  const { t } = useTranslation();
  // Sur mobile les cartes défilent avec la page : sans remonter, la nouvelle page
  // s'ouvrirait sur sa fin, là où se trouvait la pagination.
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const goTo = (target: number) => {
    onPageChange(target);
    scrollToTop();
  };
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min((page + 1) * pageSize, total);
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const isLast = to >= total;

  return (
    <div className="mt-4 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <NavButton
          label={t("pagination-back-to-top")}
          disabled={false}
          onClick={scrollToTop}
          className="md:hidden"
        >
          <FontAwesomeIcon icon="arrow-up" />
        </NavButton>
        <span className="text-sm text-grey-readable">
          {from}–{to}
          {showTotal && ` / ${total}`}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <NavButton
          label={t("pagination-first")}
          disabled={page === 0}
          onClick={() => goTo(0)}
        >
          <FontAwesomeIcon icon="angles-left" />
        </NavButton>
        <NavButton
          label={t("pagination-previous")}
          disabled={page === 0}
          onClick={() => goTo(Math.max(0, page - 1))}
        >
          <FontAwesomeIcon icon="chevron-left" />
          <span className="hidden md:inline ml-2">
            {t("pagination-previous")}
          </span>
        </NavButton>

        {showTotal && (
          <div className="hidden md:flex items-center gap-1">
            {pageItems(page, pageCount).map((item, i) =>
              item === "gap" ? (
                <span key={`gap-${i}`} className="px-1 text-grey-readable">
                  …
                </span>
              ) : (
                <button
                  key={item}
                  type="button"
                  onClick={() => goTo(item)}
                  aria-label={t("pagination-page", { page: item + 1 })}
                  aria-current={item === page ? "page" : undefined}
                  className={`min-w-[2rem] rounded px-2 py-1 ${
                    item === page
                      ? "bg-secondary text-white font-medium"
                      : "text-default-text hover:bg-grey-light"
                  }`}
                >
                  {item + 1}
                </button>
              )
            )}
          </div>
        )}

        <NavButton
          label={t("pagination-next")}
          disabled={isLast}
          onClick={() => goTo(page + 1)}
        >
          <span className="hidden md:inline mr-2">{t("pagination-next")}</span>
          <FontAwesomeIcon icon="chevron-right" />
        </NavButton>
        {showTotal && (
          <NavButton
            label={t("pagination-last")}
            disabled={isLast}
            onClick={() => goTo(pageCount - 1)}
          >
            <FontAwesomeIcon icon="angles-right" />
          </NavButton>
        )}
      </div>
    </div>
  );
};
